import { displayDALocation } from '../lib/daDisplay';
import { cleanReferralAgencies, formatLotPlan, getProRecordConflicts } from '../lib/proDisplayTrust';
import { DOCUMENT_VIEWING_ENABLED } from '../lib/documentViewingPolicy';
/**
 * ProIntelligence.jsx — Pro tier DA detail section
 *
 * Renders only for Pro/Trade/Business users when a DA has pro fields populated.
 * Receives `proData` from get_da_detail RPC (full Pro fields already gated at DB level).
 * Standard users never reach this component.
 *
 * Sections (collapsible, Supply Chain expanded by default):
 *   a) Supply Chain    — builder, architect, certifier, engineer, planner, surveyor, owner, applicant
 *   b) Specialists     — consultants JSONB array (deduped by name)
 *   c) Project         — GFA, site area, construction type, BCA class, materials, parking, existing use
 *   d) Sustainability  — BASIX, NatHERS, solar, rainwater, EV
 *   e) Planning        — zoning, LEP, height/FSR, setbacks, clause variations, referrals, planning clauses
 *   f) Site Constraints — boolean flags as pills
 *   g) Determination   — assessment officer, determination type, application type
 *   h) Tracking        — tracking_milestones timeline
 *   i) Documents       — document_list
 *   j) History         — previous DAs, modification, certificates, related applications
 *   k) Trades          — trades_required as pills (Trade tier only, hidden for Pro)
 */

import { useState } from 'react';
import { requestBrokeredContact } from '../lib/brokeredContact';
import {
  appendSourcePage,
  isSupportedSourcePdfUrl,
  parseSourceDocumentTarget,
} from '../lib/sourceDocumentRouting';

const BROKERED_CONTACT_ENABLED = process.env.NODE_ENV !== 'production'
  || process.env.REACT_APP_BROKERED_CONTACT_ENABLED === 'true';
import { addReturnParam, applicationSourceReturnHref } from '../lib/sourceReturn';

// ── Phone / Email SVG icons ───────────────────────────────────────────────────
function PhoneIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.83a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  );
}

// ── Chevron SVG ───────────────────────────────────────────────────────────────
function Chevron({ open }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, transition: 'transform 0.2s ease', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ── Collapsible section ───────────────────────────────────────────────────────
function CollapsibleSection({ title, isOpen, onToggle, children }) {
  return (
    <div style={{ marginBottom: '4px' }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          width: '100%', background: 'none', border: 'none',
          cursor: 'pointer', padding: '8px 0', textAlign: 'left',
        }}
      >
        <span style={{
          fontSize: '8px', fontWeight: '700', letterSpacing: '1px',
          textTransform: 'uppercase', padding: '2px 6px', borderRadius: '4px',
          background: '#d4a84a', color: '#0a0f1a', fontFamily: "'DM Sans',sans-serif",
          flexShrink: 0,
        }}>PRO</span>
        <span style={{
          fontSize:"var(--text-xs)", color: '#9ca3af', letterSpacing: '1.2px',
          textTransform: 'uppercase', fontWeight: '600', fontFamily: "'DM Sans',sans-serif",
        }}>{title}</span>
        <span style={{ marginLeft: 'auto' }}><Chevron open={isOpen} /></span>
      </button>
      {/* CSS grid trick: animates between 0fr and 1fr without needing a fixed height */}
      <div style={{
        display: 'grid',
        gridTemplateRows: isOpen ? '1fr' : '0fr',
        transition: 'grid-template-rows 0.25s ease',
      }}>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ paddingBottom: '12px' }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function isFieldLevelRestrictedSource(source) {
  const kind = String(source?.source_kind || '').toLowerCase();
  return kind === 'field_observation' || kind === 'field_observation_metadata';
}

function sourceAction(sourceLinks) {
  return isFieldLevelRestrictedSource(sourceLinks?.details) ? sourceLinks.details : null;
}

function restrictedContactSourceActions(sourceLinks, availability = {}) {
  if (!DOCUMENT_VIEWING_ENABLED) return [];
  return ['phone', 'email']
    .filter(type => availability?.[type] === true || isFieldLevelRestrictedSource(sourceLinks?.[type]))
    .map(type => ({ type, source: sourceLinks?.[type] }))
    .filter(item => isFieldLevelRestrictedSource(item.source));
}

function missingRestrictedContactSources(sourceLinks, availability = {}) {
  return ['phone', 'email'].filter(type => (
    availability?.[type] === true && !isFieldLevelRestrictedSource(sourceLinks?.[type])
  ));
}

function SourcePdfButton({ source, role, theme, sourceBaseUrl, sourceContext }) {
  if (!DOCUMENT_VIEWING_ENABLED || !source) return null;
  const href = sourceHref(source, sourceBaseUrl, sourceContext);
  if (!href || href === '#') return <span role="status" style={{ color: theme.textMuted, fontSize: 12 }}>Source link unavailable</span>;
  const contactType = source?.contact_type === 'phone' || source?.contact_type === 'email'
    ? source.contact_type
    : 'details';
  const buttonLabel = contactType === 'details' ? 'View source PDF' : `View ${contactType} source (PDF)`;
  return (
    <a
      href={href}
      onClick={e => e.stopPropagation()}
      title={sourceTitle(source, role, contactType)}
      aria-label={sourceTitle(source, role, contactType)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 44,
        padding: '0 10px',
        borderRadius: 6,
        border: '1px solid rgba(212,168,74,0.42)',
        background: 'rgba(212,168,74,0.10)',
        color: '#d4a84a',
        textDecoration: 'none',
        fontSize: 11,
        lineHeight: 1,
        fontWeight: 700,
        letterSpacing: 0,
        whiteSpace: 'nowrap',
        fontFamily: "'DM Sans',sans-serif",
        WebkitTapHighlightColor: 'transparent',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'rgba(212,168,74,0.18)';
        e.currentTarget.style.borderColor = 'rgba(212,168,74,0.68)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'rgba(212,168,74,0.10)';
        e.currentTarget.style.borderColor = 'rgba(212,168,74,0.42)';
        e.currentTarget.style.color = '#d4a84a';
      }}
    >
      {buttonLabel}
    </a>
  );
}

function ContactSourceUnavailable({ types = [] }) {
  if (!DOCUMENT_VIEWING_ENABLED) return null;
  if (!types.length) return null;
  const fieldLabel = types.join(' and ');
  return (
    <span
      role="status"
      title={`No value-matched public source document is available for the withheld ${fieldLabel}.`}
      style={{
        color: '#9ca3af',
        fontSize: 10,
        lineHeight: 1.25,
        maxWidth: 110,
        textAlign: 'right',
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      Contact source unavailable
    </span>
  );
}

// ── Contact row ────────────────────────────────────────────────────────────────
// Fallback: if name is null but company has a value, show company as the primary display.
// If both name and company are null/empty, the row is hidden entirely.
function BrokeredContactButton({ brokeredContact }) {
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  if (!brokeredContact?.daId || !brokeredContact?.role) return null;

  async function handleClick(event) {
    event.stopPropagation();
    if (status === 'loading' || status === 'sent' || status === 'manual_review') return;

    setStatus('loading');
    setMessage('');
    try {
      const result = await requestBrokeredContact({
        daId: brokeredContact.daId,
        role: brokeredContact.role,
      });
      const nextStatus = result.status || 'sent';
      setStatus(nextStatus);
      setMessage(result.message || (nextStatus === 'sent' ? 'Request sent' : 'Request recorded'));
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Could not request intro');
    }
  }

  const done = status === 'sent' || status === 'manual_review' || status === 'requested';
  const label = status === 'loading'
    ? 'Requesting...'
    : done
      ? 'Requested'
      : 'Request intro';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={status === 'loading' || done}
        aria-label={`Request SiteScope introduction to ${brokeredContact.role}`}
        title="Request a SiteScope-mediated introduction"
        style={{
          border: `1px solid ${done ? 'rgba(90,186,106,0.35)' : 'rgba(212,168,74,0.42)'}`,
          background: done ? 'rgba(90,186,106,0.1)' : 'rgba(212,168,74,0.08)',
          color: done ? '#76c783' : '#d4a84a',
          borderRadius: 6,
          padding: '4px 7px',
          fontSize: 11,
          fontWeight: 700,
          fontFamily: "'DM Sans',sans-serif",
          cursor: status === 'loading' || done ? 'default' : 'pointer',
          whiteSpace: 'nowrap',
          opacity: status === 'loading' ? 0.72 : 1,
        }}
      >
        {label}
      </button>
      {message && (
        <span style={{
          maxWidth: 150,
          color: status === 'error' ? '#c07040' : '#76c783',
          fontSize: 10,
          lineHeight: 1.25,
          textAlign: 'right',
          fontFamily: "'DM Sans',sans-serif",
        }}>
          {message}
        </span>
      )}
    </div>
  );
}

function ContactRow({ role, name, company, phone, email, licence, registration, type, theme, sourceLinks, hiddenContactAvailability, sourceBaseUrl, sourceContext, brokeredContact }) {
  // company is the fallback when name is absent
  const cleanName      = hasDisplayValue(name) ? name : null;
  const cleanCompany   = hasDisplayValue(company) ? company : null;
  const displayName    = cleanName || cleanCompany;
  const displayCompany = cleanName ? cleanCompany : null; // only show " — company" suffix when name is also present
  const displayPhone   = null; // Raw document observations are not verified contact channels.
  const displayEmail   = null;
  const hiddenSources = restrictedContactSourceActions(sourceLinks, hiddenContactAvailability);
  const missingHiddenSources = missingRestrictedContactSources(sourceLinks, hiddenContactAvailability);
  const detailsSource = hiddenSources.length || missingHiddenSources.length ? null : sourceAction(sourceLinks);

  if (!displayName) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
      <span style={{
        fontSize:"var(--text-xs)", color: theme.textMuted, letterSpacing: '0.8px',
        textTransform: 'uppercase', fontWeight: '600', fontFamily: "'DM Sans',sans-serif",
        flex: '0 1 40%', maxWidth: '40%', minWidth: 0, paddingTop: '2px',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{role}</span>
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize:"var(--text-sm)", color: '#e8e0d8', fontWeight: '500', fontFamily: "'DM Sans',sans-serif", lineHeight: 1.45 }}>
            {displayName}{displayCompany ? <span style={{ color: theme.textMuted }}> — {displayCompany}</span> : null}
            {hasDisplayValue(type) ? <span style={{ color: theme.textMuted, fontSize:"var(--text-xs)" }}> ({type})</span> : null}
          </span>
          {(displayPhone || displayEmail || detailsSource || hiddenSources.length || missingHiddenSources.length || brokeredContact) && (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0, marginTop: -3, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {displayPhone && (
                <a href={`tel:${phone}`} aria-label={`Call ${name || company || role}`} onClick={e => e.stopPropagation()} title={phone}
                  style={{ color: theme.textMuted, display: 'flex', alignItems: 'center', textDecoration: 'none', padding: '3px' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#d4a84a'}
                  onMouseLeave={e => e.currentTarget.style.color = theme.textMuted}
                ><PhoneIcon /></a>
              )}
              {displayEmail && (
                <a href={`mailto:${email}`} aria-label={`Email ${name || company || role}`} onClick={e => e.stopPropagation()} title={email}
                  style={{ color: theme.textMuted, display: 'flex', alignItems: 'center', textDecoration: 'none', padding: '3px' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#d4a84a'}
                  onMouseLeave={e => e.currentTarget.style.color = theme.textMuted}
                ><EmailIcon /></a>
              )}
              {detailsSource && (
                <SourcePdfButton source={detailsSource} role={role} theme={theme} sourceBaseUrl={sourceBaseUrl} sourceContext={sourceContext} />
              )}
              {hiddenSources.map(({ type: contactType, source }) => (
                <SourcePdfButton
                  key={contactType}
                  source={{ ...source, contact_type: contactType }}
                  role={role}
                  theme={theme}
                  sourceBaseUrl={sourceBaseUrl}
                  sourceContext={sourceContext}
                />
              ))}
              <ContactSourceUnavailable types={missingHiddenSources} />
              <BrokeredContactButton brokeredContact={brokeredContact} />
            </div>
          )}
        </div>
        <div style={{ color: theme.textMuted, fontSize: 11 }}>From DA document · contact details unverified</div>
        {(hasDisplayValue(licence) || hasDisplayValue(registration)) && (
          <div style={{ fontSize:"var(--text-xs)", color: theme.textMuted, marginTop: '1px', fontFamily: "'DM Sans',sans-serif" }}>
            {hasDisplayValue(licence) && <span>Licence: {licence}</span>}
            {hasDisplayValue(registration) && <span>Reg: {registration}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Trusted Party card (SBD-verified or raw DA fallback) ─────────────────────
const ROLE_LABELS = {
  builder:   'Builder',
  architect: 'Architect',
  certifier: 'Certifier',
  planner:   'Town Planner',
  engineer:  'Engineer',
  surveyor:  'Surveyor',
};
function toRoleLabel(role) {
  return ROLE_LABELS[role] || (role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Party');
}

const ROLE_LABEL_STYLE = {
  fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.8px',
  fontWeight: 600, fontFamily: "'DM Sans',sans-serif",
  flex: '0 1 40%', maxWidth: '40%', minWidth: 0, paddingTop: 2,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

function sameDisplayValue(a, b) {
  return normalizeForMatch(a || '') === normalizeForMatch(b || '');
}

function firstDisplayValue(...values) {
  return values.find(hasDisplayValue) || null;
}

function partyDisplayName(party) {
  const company = firstDisplayValue(
    party.canonical_name,
    party.company,
    party.company_name,
    party.trading_name,
    party.legal_name
  );
  const person = firstDisplayValue(
    party.person_name,
    party.contact_name,
    party.individual_name,
    party.principal_name,
    party.representative_name,
    party.name
  );

  if (hasDisplayValue(person) && hasDisplayValue(company) && !sameDisplayValue(person, company)) {
    return `${person} — ${company}`;
  }
  return company || person || party.extracted_name;
}

function partyDisplayValue(party) {
  return firstDisplayValue(
    partyDisplayName(party),
    party.extracted_name,
    party.name,
    party.company,
    party.company_name
  );
}

function hasRenderableParty(party) {
  return !!partyDisplayValue(party || {});
}

function partySecondaryLine(party) {
  const licence = firstDisplayValue(
    party.licence,
    party.license,
    party.licence_number,
    party.license_number,
    party.builder_licence,
    party.fair_trading_licence
  );
  const registration = firstDisplayValue(
    party.registration,
    party.registration_number,
    party.certifier_registration,
    party.architect_registration
  );
  const parts = [];
  if (licence) parts.push(`Licence: ${licence}`);
  if (registration) parts.push(`Reg: ${registration}`);
  if (party.abn) parts.push(`ABN ${party.abn}`);
  return parts.join(' · ');
}

// source === 'raw_da': muted row from flat DA fields, with source links for restricted personal contacts.
// source === 'sbd':    canonical name, contact icons gated by trust state.
function TrustedPartyCard({ party, theme, contactSources, restrictedContactAvailability, sourceBaseUrl, sourceContext }) {
  const roleStr = toRoleLabel(party.role);
  const restrictedRole = restrictedContactRole(party.role);
  const roleSourceLinks = restrictedRole ? contactSourcesForRole(contactSources, restrictedRole) : {};
  const roleAvailability = restrictedRole ? (safeObject(restrictedContactAvailability?.[restrictedRole]) || {}) : {};

  if (party.source !== 'sbd') {
    const displayName = partyDisplayValue(party);
    const allowRawContact = false; // DEC-009: raw fallback shows identity/role, never contacts.
    const showPhone = allowRawContact && isValidPhone(party.phone);
    const showEmail = allowRawContact && isValidEmail(party.email);
    const hiddenAvailability = !allowRawContact ? {
      phone: roleAvailability.phone === true || isValidPhone(party.phone),
      email: roleAvailability.email === true || isValidEmail(party.email),
    } : {};
    const contactSourceActions = !allowRawContact
      ? restrictedContactSourceActions(roleSourceLinks, hiddenAvailability)
      : [];
    const missingContactSources = !allowRawContact
      ? missingRestrictedContactSources(roleSourceLinks, hiddenAvailability)
      : [];
    const detailsSource = !allowRawContact && !contactSourceActions.length && !missingContactSources.length
      ? sourceAction(roleSourceLinks)
      : null;
    if (!displayName) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14, opacity: 0.62 }}>
        <span style={{ ...ROLE_LABEL_STYLE, color: theme.textMuted }}>{roleStr}</span>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--text-sm)', color: '#e8e0d8', fontWeight: 400,
            fontFamily: "'DM Sans',sans-serif", lineHeight: 1.45,
          }}>
            {displayName}
          </div>
        </div>
        {(showPhone || showEmail || detailsSource || contactSourceActions.length > 0 || missingContactSources.length > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 4, flexShrink: 0, marginTop: -10 }}>
            {showPhone && (
              <a href={`tel:${party.phone}`} aria-label={`Call ${party.canonical_name || partyDisplayValue(party) || roleStr}`} onClick={e => e.stopPropagation()} title={party.phone}
                style={{
                  color: theme.textMuted, textDecoration: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 44, height: 44, borderRadius: 8,
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#d4a84a'; e.currentTarget.style.background = 'rgba(212,168,74,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = theme.textMuted; e.currentTarget.style.background = 'transparent'; }}
              ><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.83a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg></a>
            )}
            {showEmail && (
              <a href={`mailto:${party.email}`} aria-label={`Email ${party.canonical_name || partyDisplayValue(party) || roleStr}`} onClick={e => e.stopPropagation()} title={party.email}
                style={{
                  color: theme.textMuted, textDecoration: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 44, height: 44, borderRadius: 8,
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#d4a84a'; e.currentTarget.style.background = 'rgba(212,168,74,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = theme.textMuted; e.currentTarget.style.background = 'transparent'; }}
              ><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></a>
            )}
            {detailsSource && (
              <SourcePdfButton source={detailsSource} role={roleStr} theme={theme} sourceBaseUrl={sourceBaseUrl} sourceContext={sourceContext} />
            )}
            {contactSourceActions.map(({ type: contactType, source }) => (
              <SourcePdfButton
                key={contactType}
                source={{ ...source, contact_type: contactType }}
                role={roleStr}
                theme={theme}
                sourceBaseUrl={sourceBaseUrl}
                sourceContext={sourceContext}
              />
            ))}
            <ContactSourceUnavailable types={missingContactSources} />
          </div>
        )}
      </div>
    );
  }

  // Only verified/source-confirmed SBD channels are actionable. Unknown or document-only states fail closed.
  const trustState = party.trust_state || party.directory_trust_state;
  const verifiedFieldStates = new Set(['verified_official', 'source_confirmed']);
  const phoneTrustState = party.phone_trust_state || trustState;
  const emailTrustState = party.email_trust_state || trustState;
  const displayName = partyDisplayName(party);
  const applicantTrustedBusiness = restrictedRole === 'applicant' && isBusinessApplicant({
    name: displayName,
    company: firstDisplayValue(party.company, party.company_name, party.trading_name, party.legal_name),
  });
  const allowTrustedContact = restrictedRole === 'owner' ? false : (restrictedRole === 'applicant' ? applicantTrustedBusiness : true);
  const showPhone = allowTrustedContact && isValidPhone(party.phone) && verifiedFieldStates.has(phoneTrustState);
  const showEmail = allowTrustedContact && isValidEmail(party.email) && verifiedFieldStates.has(emailTrustState);
  const hiddenAvailability = !allowTrustedContact ? {
    phone: roleAvailability.phone === true || isValidPhone(party.phone),
    email: roleAvailability.email === true || isValidEmail(party.email),
  } : {};
  const contactSourceActions = !allowTrustedContact
    ? restrictedContactSourceActions(roleSourceLinks, hiddenAvailability)
    : [];
  const missingContactSources = !allowTrustedContact
    ? missingRestrictedContactSources(roleSourceLinks, hiddenAvailability)
    : [];
  const detailsSource = !allowTrustedContact && !contactSourceActions.length && !missingContactSources.length
    ? sourceAction(roleSourceLinks)
    : null;
  const secondaryLine = partySecondaryLine(party);

  if (!hasDisplayValue(displayName)) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
      <span style={{ ...ROLE_LABEL_STYLE, color: theme.textMuted }}>{roleStr}</span>
      <div style={{ flex: '1 1 0', minWidth: 0 }}>
        <div>
          <span style={{
            fontSize: 'var(--text-sm)', color: '#e8e0d8', fontWeight: 500,
            fontFamily: "'DM Sans',sans-serif", lineHeight: 1.45,
          }}>
            {displayName}
          </span>
        </div>
        {secondaryLine && (
          <div style={{ fontSize: 11, color: theme.textDim, marginTop: 2, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.35 }}>
            {secondaryLine}
          </div>
        )}
      </div>
      {(showPhone || showEmail || detailsSource || contactSourceActions.length > 0 || missingContactSources.length > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 4, flexShrink: 0, marginTop: -10 }}>
          {showPhone && (
            <a href={`tel:${party.phone}`} aria-label={`Call ${party.canonical_name || partyDisplayValue(party) || roleStr}`} onClick={e => e.stopPropagation()} title={party.phone}
              style={{
                color: theme.textMuted, textDecoration: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 44, height: 44, borderRadius: 8,
                WebkitTapHighlightColor: 'transparent',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#d4a84a'; e.currentTarget.style.background = 'rgba(212,168,74,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = theme.textMuted; e.currentTarget.style.background = 'transparent'; }}
            ><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.83a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg></a>
          )}
          {showEmail && (
            <a href={`mailto:${party.email}`} aria-label={`Email ${party.canonical_name || partyDisplayValue(party) || roleStr}`} onClick={e => e.stopPropagation()} title={party.email}
              style={{
                color: theme.textMuted, textDecoration: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 44, height: 44, borderRadius: 8,
                WebkitTapHighlightColor: 'transparent',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#d4a84a'; e.currentTarget.style.background = 'rgba(212,168,74,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = theme.textMuted; e.currentTarget.style.background = 'transparent'; }}
            ><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></a>
          )}
          {detailsSource && (
            <SourcePdfButton source={detailsSource} role={roleStr} theme={theme} sourceBaseUrl={sourceBaseUrl} sourceContext={sourceContext} />
          )}
          {contactSourceActions.map(({ type: contactType, source }) => (
            <SourcePdfButton
              key={contactType}
              source={{ ...source, contact_type: contactType }}
              role={roleStr}
              theme={theme}
              sourceBaseUrl={sourceBaseUrl}
              sourceContext={sourceContext}
            />
          ))}
          <ContactSourceUnavailable types={missingContactSources} />
        </div>
      )}
    </div>
  );
}

// ── Detail grid item ───────────────────────────────────────────────────────────
const PLACEHOLDER_VALUES = new Set([
  '-',
  '--',
  'unknown',
  'n/a',
  'na',
  'tba',
  'not applicable',
  'not yet appointed',
  'to be confirmed',
  'to be advised',
  'not specified',
  'specified',
  'as specified',
  'as per plans',
  'refer plans',
  'see plans',
]);
const CONTACT_NOTE_RE = /^(note|refer|see|required|tbc|unknown|n\/a)\b/i;

function normalizePlaceholderValue(value) {
  return String(value || '')
    .replace(/[—–]/g, '-')
    .replace(/[.,:;]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function hasDisplayValue(value) {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return !PLACEHOLDER_VALUES.has(normalizePlaceholderValue(value));
  return true;
}

function truncateText(value, max = 120) {
  if (!hasDisplayValue(value)) return null;
  const str = String(value).replace(/\s+/g, ' ').trim();
  return str.length > max ? `${str.slice(0, max - 1).trim()}…` : str;
}

function isValidEmail(value) {
  if (!hasDisplayValue(value)) return false;
  const str = String(value).trim();
  if (CONTACT_NOTE_RE.test(str) || str.includes(',')) return false;
  return /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(str);
}

function isValidPhone(value) {
  if (!hasDisplayValue(value)) return false;
  const str = String(value).trim();
  if (CONTACT_NOTE_RE.test(str) || str.includes(',')) return false;
  const digits = str.replace(/\D/g, '');
  if (!/^[+\d\s().-]+$/.test(str)) return false;
  if (/^61/.test(digits)) return /^61[23478]\d{8}$/.test(digits);
  return /^(?:0[23478]\d{8}|1[38]00\d{6}|13\d{4})$/.test(digits);
}

function hasPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

function hasContactValue(...values) {
  return values.some(hasDisplayValue);
}

function uniqueTextList(items, limit = Infinity) {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const text = typeof item === 'string' ? item : String(item || '');
    const cleaned = text.replace(/\s+/g, ' ').trim();
    if (!hasDisplayValue(cleaned)) continue;
    const key = normalizeForMatch(cleaned);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(cleaned);
    if (out.length >= limit) break;
  }
  return out;
}

function uniqueObjectList(items, keyFn, limit = Infinity) {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const key = normalizeForMatch(keyFn(item));
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
    if (out.length >= limit) break;
  }
  return out;
}

function GridItem({ label, value, amber, theme }) {
  if (!hasDisplayValue(value)) return null;
  return (
    <div>
      <div style={{ fontSize:"var(--text-xs)", color: theme.textMuted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '2px', fontFamily: "'DM Sans',sans-serif" }}>{label}</div>
      <div style={{ fontSize:"var(--text-sm)", color: amber ? '#d4a84a' : '#e8e0d8', fontWeight: amber ? '700' : '500', fontFamily: "'DM Sans',sans-serif" }}>{value}</div>
    </div>
  );
}

function relatedRecordNumber(record) {
  if (typeof record === 'string') return record;
  return record?.number || record?.da_number || record?.council_application_number || record?.id || null;
}

function relatedRecordMeta(record) {
  if (!record || typeof record === 'string') return [];
  const parts = [];
  if (hasDisplayValue(record.type)) parts.push(record.type);
  if (hasPositiveNumber(record.doc_count)) {
    parts.push(`${record.doc_count} doc${Number(record.doc_count) === 1 ? '' : 's'}`);
  }
  if (hasDisplayValue(record.status)) parts.push(record.status);
  return parts;
}

function RelatedRecordList({ label, records, theme }) {
  const renderableRecords = safeArray(records).filter(record => hasDisplayValue(relatedRecordNumber(record)));
  if (!renderableRecords.length) return null;

  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <div style={{ fontSize:"var(--text-xs)", color: theme.textMuted, fontFamily: "'DM Sans',sans-serif", marginBottom: '5px' }}>{label}</div>
      {renderableRecords.map((record, i) => {
        const number = relatedRecordNumber(record);
        const url = typeof record === 'object' ? record.url || record.link || null : null;
        const meta = relatedRecordMeta(record);
        const isLast = i === renderableRecords.length - 1;
        return (
          <div key={`${number}-${i}`} style={{ fontSize:"var(--text-sm)", fontFamily: "'DM Sans',sans-serif", marginBottom: isLast ? 0 : '6px', lineHeight: 1.45 }}>
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                style={{ color: '#d4a84a', textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >
                {truncateText(number, 80)}
              </a>
            ) : (
              <span style={{ color: '#e8e0d8' }}>{truncateText(number, 80)}</span>
            )}
            {meta.length > 0 && (
              <span style={{ color: theme.textMuted }}>{' \u2014 '}{meta.map(part => truncateText(part, 54)).join(' · ')}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Consultant dedup helpers ──────────────────────────────────────────────────

const CORP_SUFFIX_RE = /\b(proprietary\s+limited|pty\.?\s*ltd\.?|pty|ltd\.?|limited)\b/gi;
const BUSINESS_APPLICANT_RE = /\b(pty\.?\s*ltd\.?|proprietary\s+limited|limited|ltd\.?|company|co\.?|group|holdings|developments?|builders?|building|construction|constructions|homes?|designs?|planning|architects?|engineering|surveying|certifiers?|consultants?|consulting|plumbing|electrical|earthmoving|landscaping|trust|corporation|corp\.?|inc\.?)\b/i;

// Normalise a name to a stable match key (lowercase, strip corporate suffixes)
function normalizeForMatch(str) {
  return (str || '')
    .toLowerCase()
    .replace(CORP_SUFFIX_RE, '')
    .replace(/[.,]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function valuesOverlap(a, b) {
  const left = normalizeForMatch(a);
  const right = normalizeForMatch(b);
  return !!left && !!right && (left === right || left.includes(right) || right.includes(left));
}

function isBusinessApplicant({ name, company }) {
  return hasDisplayValue(company) || BUSINESS_APPLICANT_RE.test(String(name || ''));
}

function restrictedContactRole(role) {
  const key = normalizeForMatch(role || '');
  if (key.includes('owner')) return 'owner';
  if (key.includes('applicant')) return 'applicant';
  return null;
}

function contactTypeFromField(field) {
  const key = normalizeForMatch(field || '');
  if (key.endsWith('phone')) return 'phone';
  if (key.endsWith('email')) return 'email';
  if (key.endsWith('name') || key.endsWith('company')) return 'details';
  return null;
}

function resolveSourceUrl(raw, baseUrl) {
  const value = firstDisplayValue(raw);
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.toString();
  } catch {}

  if (!String(value).startsWith('/')) return value;

  try {
    const base = new URL(baseUrl);
    if (base.protocol !== 'https:') return value;
    return new URL(value, base.origin).toString();
  } catch {
    return value;
  }
}

function sourceUrl(source, baseUrl) {
  const raw = firstDisplayValue(
    source?.document_url,
    source?.source_url,
    source?.original_url,
    source?.url,
    source?.link
  );
  return resolveSourceUrl(raw, baseUrl);
}

function normaliseDocumentItem(doc, index, baseUrl) {
  const rawUrl = typeof doc === 'string'
    ? doc
    : firstDisplayValue(doc?.url, doc?.link, doc?.original_url, doc?.download_url);
  const url = resolveSourceUrl(rawUrl, baseUrl);
  let name = typeof doc === 'string'
    ? null
    : firstDisplayValue(doc?.name, doc?.title, doc?.document_name, doc?.description);

  if (!name && url) {
    try {
      const parsed = new URL(url);
      const fileName = parsed.searchParams.get('fileName') || parsed.pathname.split('/').pop();
      if (fileName) name = decodeURIComponent(fileName);
    } catch {}
  }

  return {
    name: name || `Source PDF ${index + 1}`,
    url,
  };
}

function addSourceContextParams(params, context = {}) {
  if (context.da_id) params.set('da_id', context.da_id);
  if (context.referer) params.set('referer', context.referer);
  if (context.da_number) params.set('da_number', context.da_number);
  if (context.address) params.set('address', context.address);
}

function sourceViewerHref(url, label, context, page) {
  if (!url || url === '#') return null;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (!isSupportedSourcePdfUrl(parsed.toString())) return null;
  const params = new URLSearchParams({ url });
  if (label) params.set('label', label);
  appendSourcePage(params, page);
  addSourceContextParams(params, context);
  return addReturnParam(`/source-pdf?${params.toString()}`, applicationSourceReturnHref(context?.da_id));
}

function sourceHref(source, baseUrl, context) {
  if (!DOCUMENT_VIEWING_ENABLED) return null;
  const target = parseSourceDocumentTarget(
    sourceUrl(source, baseUrl),
    source?.page_number || source?.page
  );
  const { url, page } = target;
  if (!url || url === '#') return '#';
  const label = firstDisplayValue(source?.document_name, source?.document_title, source?.name, source?.title);
  const viewer = sourceViewerHref(url, label, context, page);
  if (viewer) return viewer;
  const params = new URLSearchParams({ url });
  if (label) params.set('label', label);
  appendSourcePage(params, page);
  addSourceContextParams(params, context);
  return addReturnParam(`/source-document?${params.toString()}`, applicationSourceReturnHref(context?.da_id));
}

const PDF_INTELLIGENCE_FIELD_LABELS = Object.freeze({
  site_area: 'Site area',
  zoning: 'Zoning',
  lot_number: 'Lot number',
  deposited_plan_number: 'Deposited plan',
  strata_plan_number: 'Strata plan',
  property_title_reference: 'Title reference',
  wastewater_system_type: 'Wastewater system',
  wastewater_disposal_method: 'Wastewater disposal',
  wastewater_design_population: 'Design population',
  wastewater_design_flow: 'Design flow',
  basix_certificate_number: 'BASIX certificate',
  nathers_rating: 'NatHERS rating',
  proposal_type: 'Proposal type',
  estimated_cost: 'Estimated cost',
  cost_of_development: 'Development cost',
  dwellings_existing: 'Existing dwellings',
  dwellings_proposed: 'Proposed dwellings',
  dwellings_total: 'Total dwellings',
  storeys_existing: 'Existing storeys',
  storeys_proposed: 'Proposed storeys',
  storeys_total: 'Total storeys',
  gross_floor_area: 'Gross floor area',
  building_classification: 'Building classification',
  swimming_pool_type: 'Swimming pool',
});

const PDF_GFA_SCOPE_LABELS = Object.freeze({
  existing: 'Existing gross floor area',
  additional: 'Additional gross floor area',
  resulting_total: 'Total gross floor area after works',
});

function displayablePdfFacts(report) {
  return safeArray(report?.facts).filter(fact => (
    hasDisplayValue(fact?.field_name) && hasDisplayValue(fact?.value)
    && (fact.field_name !== 'gross_floor_area'
      || Object.prototype.hasOwnProperty.call(PDF_GFA_SCOPE_LABELS, fact.gfa_scope))
  ));
}

function pdfIntelligenceFactValue(fact) {
  const value = firstDisplayValue(fact?.value);
  if (!value) return null;
  const unit = firstDisplayValue(fact?.unit);
  if (!unit) return value;
  const normalisedUnit = unit === 'm2' ? 'm²' : unit;
  return `${value} ${normalisedUnit}`;
}

function PdfDocumentIntelligence({ report, theme, sourceBaseUrl, sourceContext }) {
  const facts = displayablePdfFacts(report);
  if (!facts.length) return null;
  return (
    <div>
      <div style={{ fontSize: 'var(--text-xs)', color: theme.textMuted, lineHeight: 1.5, marginBottom: 10, fontFamily: "'DM Sans',sans-serif" }}>
        Independently reviewed facts from the lodged source documents. Contact details are not included.
      </div>
      {facts.map((fact, index) => {
        const label = (fact.field_name === 'gross_floor_area' && PDF_GFA_SCOPE_LABELS[fact.gfa_scope])
          || PDF_INTELLIGENCE_FIELD_LABELS[fact.field_name]
          || String(fact.field_name).replace(/_/g, ' ');
        const href = sourceHref({
          source_url: fact.source_url,
          document_name: fact.document_ref || 'Source document',
          page_number: fact.page_number,
        }, sourceBaseUrl, sourceContext);
        return (
          <div key={`${fact.field_name}-${index}`} style={{
            padding: '9px 0',
            borderBottom: index === facts.length - 1 ? 'none' : '1px solid #1e2538',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}>
            <div>
              <div style={{ color: theme.textDim, fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.6px', fontFamily: "'DM Sans',sans-serif" }}>
                {label}
              </div>
              <div style={{ color: '#e8e0d8', fontSize: 'var(--text-sm)', marginTop: 3, fontFamily: "'DM Sans',sans-serif" }}>
                {pdfIntelligenceFactValue(fact)}
              </div>
            </div>
            {href && href !== '#' && (
              <a href={href} onClick={event => event.stopPropagation()}
                style={{ color: '#d4a84a', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap', textDecoration: 'none', fontFamily: "'DM Sans',sans-serif" }}>
                Source{Number(fact.page_number) > 0 ? ` · p${Math.trunc(Number(fact.page_number))}` : ''}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

function sourceTitle(source, role, type) {
  const typeLabel = type === 'details' ? 'details' : type;
  const label = `${toRoleLabel(String(role || '').toLowerCase())} ${typeLabel}`;
  const page = Number(source?.page_number || source?.page);
  const documentName = firstDisplayValue(source?.document_name, source?.document_title, source?.name, source?.title);
  const docPart = documentName ? ` in ${documentName}` : '';
  const pagePart = Number.isFinite(page) && page > 0 ? `, page ${Math.trunc(page)}` : '';
  return `View public source for ${label}${docPart}${pagePart}`;
}

function normaliseContactSourceItem(item) {
  if (!item || typeof item !== 'object') return null;
  const role = restrictedContactRole(item.role || item.party_role || item.field_name || item.field);
  const type = item.contact_type || item.type || contactTypeFromField(item.field || item.field_name);
  const url = sourceUrl(item);
  if (!role || !['phone', 'email', 'details'].includes(type) || !url) return null;
  return { ...item, role, contact_type: type };
}

function contactSourcesForRole(sourceMap, role) {
  const key = restrictedContactRole(role);
  return key ? (sourceMap?.[key] || {}) : {};
}

function buildRestrictedContactSourceMap(data) {
  const rawSources = [
    ...safeArray(data?.restricted_contact_sources),
    ...safeArray(data?.personal_contact_sources),
    ...safeArray(data?.owner_applicant_contact_sources),
    ...safeArray(data?.contact_source_links),
  ];
  const map = {};

  for (const raw of rawSources) {
    const source = normaliseContactSourceItem(raw);
    if (!source) continue;
    if (!map[source.role]) map[source.role] = {};
    if (!map[source.role][source.contact_type]) {
      map[source.role][source.contact_type] = source;
    }
  }

  return map;
}

function documentName(source) {
  return firstDisplayValue(
    source?.document_name,
    source?.document_title,
    source?.name,
    source?.title,
    source?.description,
    source?.source_ref
  );
}

function sourceDocumentPriority(source) {
  const name = String(documentName(source) || sourceUrl(source) || '').toLowerCase();
  if (/\b(application|applicant|owner|pre-da|form|statement|see)\b/.test(name)) return 0;
  if (/\b(basix|plans?|drawing|elevation|section|shadow|stormwater|landscape)\b/.test(name)) return 3;
  return 1;
}

function publicDocumentFallback(data) {
  const baseUrl = sourceUrl(data);
  const document = [...safeArray(data?.document_list)]
    .filter(doc => sourceUrl(doc, baseUrl))
    .sort((a, b) => sourceDocumentPriority(a) - sourceDocumentPriority(b))[0];
  if (document) {
    return {
      role: null,
      contact_type: 'details',
      document_url: sourceUrl(document, baseUrl),
      document_name: documentName(document) || 'Council document',
      source_kind: 'document_list_fallback',
    };
  }

  if (baseUrl) {
    return {
      role: null,
      contact_type: 'details',
      document_url: baseUrl,
      document_name: 'Council source',
      source_kind: 'da_source_fallback',
    };
  }

  return null;
}

function addRestrictedContactFallbacks(sourceMap, data) {
  const fallback = publicDocumentFallback(data);
  if (!fallback) return sourceMap;

  const next = {
    ...sourceMap,
    owner: { ...(sourceMap.owner || {}) },
    applicant: { ...(sourceMap.applicant || {}) },
  };

  if (!next.owner.details) {
    next.owner.details = { ...fallback, role: 'owner' };
  }

  if (!next.applicant.details) {
    next.applicant.details = { ...fallback, role: 'applicant' };
  }

  return next;
}

function flatSupplyChainContacts(d) {
  const applicantBusiness = isBusinessApplicant({
    name: d.applicant_name,
    company: d.applicant_company,
  });

  return [
    { role: 'builder', name: d.builder_name, company: d.builder_company, phone: d.builder_phone, email: d.builder_email },
    { role: 'architect', name: d.architect_name, company: d.architect_company, phone: d.architect_phone, email: d.architect_email },
    { role: 'certifier', name: d.certifier_name, company: d.certifier_company, phone: d.certifier_phone, email: d.certifier_email },
    { role: 'owner', name: d.owner_name },
    {
      role: 'applicant',
      name: d.applicant_name,
      company: d.applicant_company,
      phone: applicantBusiness ? d.applicant_phone : null,
      email: applicantBusiness ? d.applicant_email : null,
      is_business_applicant: applicantBusiness,
    },
  ].filter(contact => hasDisplayValue(contact.name) || hasDisplayValue(contact.company));
}

function partyMatchesFlatContact(party, contact) {
  const partyRole = normalizeForMatch(party.role);
  const contactRole = normalizeForMatch(contact.role);
  if (partyRole && contactRole && partyRole !== contactRole) return false;

  const partyNames = [
    party.canonical_name,
    party.extracted_name,
    party.company,
    party.company_name,
    party.trading_name,
    party.legal_name,
    party.person_name,
    party.contact_name,
    party.name,
  ].filter(hasDisplayValue);
  const contactNames = [contact.company, contact.name].filter(hasDisplayValue);

  return partyNames.some(partyName =>
    contactNames.some(contactName => valuesOverlap(partyName, contactName))
  );
}

function augmentTrustedPartiesWithFlatContacts(parties, d) {
  const flatContacts = flatSupplyChainContacts(d || {});
  if (!Array.isArray(parties) || parties.length === 0 || flatContacts.length === 0) return parties || [];

  const matchedFlatContacts = new Set();
  const augmentedParties = parties.map(party => {
    const contact = flatContacts.find(candidate => partyMatchesFlatContact(party, candidate));
    if (!contact) return party;
    matchedFlatContacts.add(contact);

    const next = { ...party };
    if (hasDisplayValue(contact.name) && !hasDisplayValue(next.contact_name) && !hasDisplayValue(next.person_name)) {
      next.contact_name = contact.name;
    }
    if (hasDisplayValue(contact.company) && !hasDisplayValue(next.company) && !hasDisplayValue(next.company_name)) {
      next.company = contact.company;
    }
    // Identity matching does not verify the contact values of a raw DA record.
    return next;
  });

  const extraFlatContacts = flatContacts
    .filter(contact => !matchedFlatContacts.has(contact))
    .map(contact => ({
      source: 'raw_da',
      role: contact.role,
      contact_name: contact.name,
      company: contact.company,
      phone: contact.phone,
      email: contact.email,
      is_business_applicant: contact.is_business_applicant,
    }));

  return [...augmentedParties, ...extraFlatContacts];
}

// Known acronyms that should stay uppercase even inside a title-cased string
const ACRONYMS = new Set([
  'NSW','ACT','QLD','VIC','SA','WA','TAS','NT',
  'RFS','AHIMS','BASIX','EPA','DPE','BCA','NCC',
  'SEPP','LEP','DCP','DA','CC','CDC','OEH','SES',
  'ABN','ACN','NATHERS','FSR','AWC','HERA','NDC','BPAD',
]);

// Convert ALL-CAPS strings to Title Case, preserving known acronyms
function toTitleCase(str) {
  if (!str) return str;
  const trimmed = str.trim();
  // Only transform if the string is entirely uppercase
  const isAllCaps = trimmed === trimmed.toUpperCase() && /[A-Z]{2,}/.test(trimmed);
  if (!isAllCaps) return trimmed;
  return trimmed.split(/\s+/).map(word => {
    const core = word.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (ACRONYMS.has(core)) return core;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
}

// Strip corporate suffixes from a display name and title-case it
function cleanDisplayName(str) {
  if (!str) return str;
  const cleaned = str
    .replace(/\bProprietary\s+Limited\b/gi, '')
    .replace(/\bPty\.?\s*Ltd\.?\b/gi, '')
    .replace(/\bLimited\b/gi, '')
    .replace(/[,.\s]+$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return toTitleCase(cleaned) || toTitleCase(str.trim());
}

// Pick the most concise, readable role label from a set of raw role strings.
// - Dedupes case-insensitively
// - Finds a common suffix (e.g. "Assessor") then combines unique prefixes: "Energy / BASIX Assessor"
// - Strips redundant prefixes that are substrings of simpler ones
// - Caps at 40 characters
function pickBestRole(roles) {
  if (!roles || roles.length === 0) return 'Consultant';
  // Dedup case-insensitively, preserve first-seen casing
  const seenR = new Set();
  const unique = [];
  for (const r of roles) {
    if (!r) continue;
    const k = r.toLowerCase().trim();
    if (!seenR.has(k)) { seenR.add(k); unique.push(r.trim()); }
  }
  if (unique.length === 0) return 'Consultant';
  if (unique.length === 1) return unique[0].length <= 40 ? unique[0] : unique[0].slice(0, 37) + '…';

  // Find the most common last word across all roles (candidate suffix)
  const lastWordCounts = {};
  for (const r of unique) {
    const w = r.split(/\s+/).pop().toLowerCase();
    lastWordCounts[w] = (lastWordCounts[w] || 0) + 1;
  }
  const [topSuffix, topCount] = Object.entries(lastWordCounts)
    .sort((a, b) => b[1] - a[1])[0];

  if (topCount >= Math.ceil(unique.length * 0.5)) {
    const suffix = topSuffix.charAt(0).toUpperCase() + topSuffix.slice(1);
    // Extract the prefix part of each role (everything before the suffix word)
    const prefixes = unique.map(r => {
      const words = r.split(/\s+/);
      return words[words.length - 1].toLowerCase() === topSuffix
        ? words.slice(0, -1).join(' ').trim()
        : r.trim();
    }).filter(Boolean);
    // Dedup prefixes case-insensitively
    const seenP = new Map();
    for (const p of prefixes) seenP.set(p.toLowerCase(), p);
    const deduped = [...seenP.values()];
    // Drop any prefix that is a substring of a simpler (shorter) prefix already present
    const filtered = deduped.filter(p => {
      const pLow = p.toLowerCase();
      return !deduped.some(other => {
        const oLow = other.toLowerCase();
        return oLow !== pLow && pLow.includes(oLow);
      });
    });
    const combined = ((filtered.length ? filtered : deduped).slice(0, 3).join(' / ') + ' ' + suffix).trim();
    if (combined.length <= 40) return combined;
  }

  // Fallback: longest (most specific) role, truncated
  const sorted = [...unique].sort((a, b) => b.length - a.length);
  return sorted[0].length <= 40 ? sorted[0] : sorted[0].slice(0, 37) + '…';
}

// ── Dedup consultants ──────────────────────────────────────────────────────────
// Groups entries by normalised name (case + corporate suffix invariant).
// If name === company (same entity after normalisation), shows only one cleaned name.
// Roles are combined intelligently via pickBestRole.
function deduplicateConsultants(raw) {
  if (!Array.isArray(raw)) return [];
  const map = new Map(); // normalizedKey → { displayEntry, roles[] }

  for (const c of raw) {
    const rawName    = c.name    && c.name    !== '—' ? c.name    : null;
    const rawCompany = c.company && c.company !== '—' ? c.company : null;
    if (!rawName && !rawCompany) continue;

    const normName    = normalizeForMatch(rawName    || '');
    const normCompany = normalizeForMatch(rawCompany || '');
    const matchKey    = normName || normCompany;

    // Build cleaned display fields
    let displayName, displayCompany;
    if (normName && normCompany && normName === normCompany) {
      // Same entity — show one clean name, no separate company label
      displayName    = cleanDisplayName(rawCompany || rawName);
      displayCompany = null;
    } else {
      displayName    = rawName    ? cleanDisplayName(rawName)    : cleanDisplayName(rawCompany);
      displayCompany = rawName && rawCompany ? cleanDisplayName(rawCompany) : null;
    }

    if (map.has(matchKey)) {
      const existing = map.get(matchKey);
      if (c.role) existing.roles.push(c.role);
      if (!existing.entry.phone && c.phone) existing.entry.phone = c.phone;
      if (!existing.entry.email && c.email) existing.entry.email = c.email;
    } else {
      map.set(matchKey, {
        entry: { ...c, name: displayName, company: displayCompany },
        roles: c.role ? [c.role] : [],
      });
    }
  }

  return [...map.values()].map(({ entry, roles }) => ({
    ...entry,
    role: pickBestRole(roles),
  }));
}

const NON_SPECIALIST_RE = /\b(council|department|dpie|dpe|dph&i|rural fire service|essential energy|local aboriginal land council|biodiversity conservation division|conservation programs|heritage and regulation group|regional assessments|landowner representative|contact officer|team leader|manager sustainable|accrediting organisation|sample analysis laboratory)\b/i;
const GENERIC_ROLE_RE = /\b(required|requirement|consultant required|assessor accrediting organisation)\b/i;
const SPECIALIST_ROLE_ORDER = [
  'planner',
  'architect',
  'engineer',
  'geotechnical',
  'stormwater',
  'arborist',
  'landscape',
  'biodiversity',
  'ecology',
  'bushfire',
  'contamination',
  'heritage',
  'energy',
  'nathers',
];

function compactEntityKey(consultant) {
  const company = consultant.company || '';
  const name = consultant.name || '';
  return normalizeForMatch(company || name).replace(/\s+/g, '');
}

function isLikelySpecialist(consultant) {
  const role = consultant.role || '';
  const name = consultant.name || '';
  const company = consultant.company || '';
  if (!hasDisplayValue(name) && !hasDisplayValue(company)) return false;
  if (GENERIC_ROLE_RE.test(role)) return false;
  if (CONTACT_NOTE_RE.test(consultant.email || '')) return false;
  if (!company && /^[A-Z]{2,12}$/.test(String(name).trim())) return false;
  const blob = `${role} ${name} ${company}`;
  return !NON_SPECIALIST_RE.test(blob);
}

function specialistRank(consultant) {
  const role = normalizeForMatch(consultant.role || '');
  const idx = SPECIALIST_ROLE_ORDER.findIndex(token => role.includes(token));
  return idx === -1 ? 99 : idx;
}

function prepareSpecialists(raw) {
  const consultants = deduplicateConsultants(raw)
    .filter(isLikelySpecialist)
    .sort((a, b) => specialistRank(a) - specialistRank(b));

  return uniqueObjectList(
    consultants,
    c => `${compactEntityKey(c)} ${normalizeForMatch(c.role || '')}`,
    10
  );
}

// ── JSONB helpers ─────────────────────────────────────────────────────────────
// Handles values that may come back as parsed arrays/objects or as JSON strings.
function safeArray(val) {
  if (!val) return [];
  if (typeof val === 'string') { try { val = JSON.parse(val); } catch { return []; } }
  return Array.isArray(val) && val.length > 0 ? val : [];
}

function safeObject(val) {
  if (!val) return null;
  if (typeof val === 'string') { try { val = JSON.parse(val); } catch { return null; } }
  return val && typeof val === 'object' && !Array.isArray(val) ? val : null;
}

export function hasProIntelligenceContent(proData, tier) {
  if (!proData) return false;
  if (proData.pro_snapshot_missing) return false;
  const d = proData;
  const supplyChain = safeArray(d.supply_chain).filter(hasRenderableParty);
  const hasReviewedPdfReport = displayablePdfFacts(d.pdf_intelligence_report).length > 0;

  const hasSupplyChain = !!(
    supplyChain.length > 0 ||
    hasContactValue(d.builder_name, d.builder_company) ||
    hasContactValue(d.architect_name, d.architect_company) ||
    hasContactValue(d.certifier_name, d.certifier_company) ||
    hasContactValue(d.engineer_name, d.engineer_company) ||
    hasContactValue(d.planner_name, d.planner_company) ||
    hasContactValue(d.surveyor_name, d.surveyor_company) ||
    hasContactValue(d.owner_name) ||
    hasContactValue(d.applicant_name, d.applicant_company)
  );

  const consultants = prepareSpecialists(d.consultants);
  const hasSpecialists = consultants.length > 0;

  const hasProject = !!(
    hasPositiveNumber(d.gross_floor_area) || hasPositiveNumber(d.site_area) ||
    hasDisplayValue(d.construction_type) || hasDisplayValue(d.building_classification) ||
    hasDisplayValue(d.construction_materials) || hasPositiveNumber(d.car_spaces) ||
    hasPositiveNumber(d.accessible_spaces) || hasPositiveNumber(d.bicycle_spaces) ||
    hasDisplayValue(d.existing_use) || hasDisplayValue(d.conditions_summary)
  );

  const hasSustainability = !!(
    hasDisplayValue(d.basix_certificate_number) || hasPositiveNumber(d.nathers_rating) ||
    hasPositiveNumber(d.basix_water_score) || hasPositiveNumber(d.basix_thermal_score) || hasPositiveNumber(d.basix_energy_score) ||
    d.solar_panels === true || d.rainwater_tank === true || d.ev_charging === true ||
    hasDisplayValue(d.green_star_rating) || hasDisplayValue(d.nathers_assessor) || d.section_j_compliant === true
  );

  const rawClauseVariations = safeArray(d.clause_variations);
  const rawReferralAgencies = cleanReferralAgencies(d.referral_agencies);
  const rawPlanningClauses  = safeArray(d.planning_clauses_referenced);
  const clauseVariations = uniqueObjectList(
    rawClauseVariations,
    cv => `${cv?.clause || ''} ${cv?.variation_sought || ''}`,
    6
  );
  const referralAgencies = uniqueTextList(rawReferralAgencies, 8);
  const planningClauses  = uniqueTextList(rawPlanningClauses, 10);
  const setbacks         = safeObject(d.setbacks);
  const setbackEntries   = setbacks
    ? Object.entries(setbacks).filter(([, v]) => hasDisplayValue(v))
    : [];
  const hasPlanning = !!(
    hasDisplayValue(d.zoning) || hasDisplayValue(d.lep_name) ||
    hasPositiveNumber(d.height_limit) || hasPositiveNumber(d.height_proposed) ||
    hasPositiveNumber(d.fsr_limit) || hasPositiveNumber(d.fsr_proposed) ||
    hasPositiveNumber(d.submissions_received) || hasPositiveNumber(d.objections_count) ||
    hasPositiveNumber(d.conditions_count) ||
    clauseVariations.length || referralAgencies.length || planningClauses.length || setbackEntries.length
  );

  const hasConstraints = [
    'flood_prone',
    'bushfire_prone',
    'heritage_listed',
    'coastal_zone',
    'contaminated',
    'biodiversity_triggered',
    'acid_sulfate_soils',
    'aboriginal_heritage',
  ].some(key => d[key] === true);

  const hasDetermination = !!(
    hasDisplayValue(d.assessment_officer) || hasDisplayValue(d.determination_type) ||
    hasDisplayValue(d.application_type)
  );

  const hasTracking = safeArray(d.tracking_milestones).some(m => (
    hasDisplayValue(m?.description || m?.event || m?.milestone || m?.name || (typeof m === 'string' ? m : null))
  ));
  const hasDocuments = DOCUMENT_VIEWING_ENABLED && (safeArray(d.document_list).some(doc => (
    hasDisplayValue(doc.name || doc.title || doc.document_name || (typeof doc === 'string' ? doc : null))
  )) || safeArray(d.pdf_source_urls).some(hasDisplayValue));
  const hasHistory = !!(
    safeArray(d.previous_da_numbers).length ||
    safeArray(d.related_certificates).length ||
    safeArray(d.related_applications).length
  );
  const trades = tier === 'trade' ? safeArray(d.trades_required).filter(hasDisplayValue) : [];

  return !!(
    hasSupplyChain || hasSpecialists || hasProject || hasSustainability || hasReviewedPdfReport ||
    hasPlanning || hasConstraints || hasDetermination || hasTracking ||
    hasDocuments || hasHistory || trades.length
  );
}

export function isProSnapshotMissing(proData) {
  return Boolean(proData?.pro_snapshot_missing);
}

function ProSnapshotPending({ theme }) {
  return (
    <div
      style={{
        marginTop: 14,
        padding: '12px 0 4px',
        borderTop: '2px solid #d4a84a',
        color: theme?.textMuted || '#9ca3af',
        fontSize: '13px',
        lineHeight: 1.55,
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      <div style={{ color: '#d4a84a', fontWeight: 700, marginBottom: 4 }}>
        Pro Intelligence is being prepared
      </div>
      <div>
        This DA is in the enrichment queue. Check back shortly.
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ProIntelligence({ proData, theme, tier, trustedParties = [], showEmpty = false, appRecord = null }) {
  const [open, setOpen] = useState({
    supplyChain:    true,
    specialists:    false,
    project:        false,
    documentIntelligence: true,
    sustainability: false,
    planning:       false,
    constraints:    false,
    determination:  false,
    tracking:       false,
    documents:      false,
    history:        false,
    trades:         false,
  });

  const tog = key => setOpen(p => ({ ...p, [key]: !p[key] }));

  if (!proData && !trustedParties.length) {
    return (
      <div style={{ padding: '16px 0', color: theme?.textMuted || '#9ca3af', fontSize: '13px', fontFamily: "'DM Sans',sans-serif" }}>
        Pro Intelligence data is not yet available for this DA.
      </div>
    );
  }

  const d = proData || {};
  if (isProSnapshotMissing(d)) {
    return <ProSnapshotPending theme={theme} />;
  }

  const sourceBaseUrl = sourceUrl(d);
  const sourceContext = {
    da_id: firstDisplayValue(d.canonical_da_id, d.id),
    referer: sourceBaseUrl,
    da_number: firstDisplayValue(d.da_number, d.council_application_number),
    address: displayDALocation(d),
  };
  const restrictedContactSources = addRestrictedContactFallbacks(buildRestrictedContactSourceMap(d), d);
  const restrictedContactAvailability = safeObject(d.restricted_contact_availability) || {};
  const applicantIsBusiness = isBusinessApplicant({ name: d.applicant_name, company: d.applicant_company });
  const supplyChainParties = safeArray(d.supply_chain);
  const baseTrustedParties = supplyChainParties.length > 0 ? supplyChainParties : trustedParties;
  const displayTrustedParties = (supplyChainParties.length > 0
    ? supplyChainParties
    : augmentTrustedPartiesWithFlatContacts(baseTrustedParties, d))
    .filter(hasRenderableParty);

  // ── a) Supply Chain ──────────────────────────────────────────────────────────
  const hasSupplyChain = !!(
    displayTrustedParties.length > 0 ||
    hasContactValue(d.builder_name, d.builder_company) ||
    hasContactValue(d.architect_name, d.architect_company) ||
    hasContactValue(d.certifier_name, d.certifier_company) ||
    hasContactValue(d.engineer_name, d.engineer_company) ||
    hasContactValue(d.planner_name, d.planner_company) ||
    hasContactValue(d.surveyor_name, d.surveyor_company) ||
    hasContactValue(d.owner_name) ||
    hasContactValue(d.applicant_name, d.applicant_company)
  );

  // ── b) Specialists ───────────────────────────────────────────────────────────
  const consultants = prepareSpecialists(d.consultants);
  const hasSpecialists = consultants.length > 0;
  const hasReviewedPdfReport = displayablePdfFacts(d.pdf_intelligence_report).length > 0;

  // ── c) Project details ───────────────────────────────────────────────────────
  const hasProject = !!(
    hasPositiveNumber(d.gross_floor_area) || hasPositiveNumber(d.site_area) ||
    hasDisplayValue(d.construction_type) || hasDisplayValue(d.building_classification) ||
    hasDisplayValue(d.construction_materials) || hasPositiveNumber(d.car_spaces) ||
    hasPositiveNumber(d.accessible_spaces) || hasPositiveNumber(d.bicycle_spaces) ||
    hasDisplayValue(d.existing_use) || hasDisplayValue(d.conditions_summary)
  );

  // ── d) Sustainability ────────────────────────────────────────────────────────
  const hasSustainability = !!(
    hasDisplayValue(d.basix_certificate_number) || hasPositiveNumber(d.nathers_rating) ||
    hasPositiveNumber(d.basix_water_score) || hasPositiveNumber(d.basix_thermal_score) || hasPositiveNumber(d.basix_energy_score) ||
    d.solar_panels === true || d.rainwater_tank === true || d.ev_charging === true ||
    hasDisplayValue(d.green_star_rating) || hasDisplayValue(d.nathers_assessor) || d.section_j_compliant === true
  );

  // ── e) Planning ──────────────────────────────────────────────────────────────
  const rawClauseVariations = safeArray(d.clause_variations);
  const rawReferralAgencies = cleanReferralAgencies(d.referral_agencies);
  const rawPlanningClauses  = safeArray(d.planning_clauses_referenced);
  const clauseVariations = uniqueObjectList(
    rawClauseVariations,
    cv => `${cv?.clause || ''} ${cv?.variation_sought || ''}`,
    6
  );
  const referralAgencies = uniqueTextList(rawReferralAgencies, 8);
  const planningClauses  = uniqueTextList(rawPlanningClauses, 10);
  const setbacks         = safeObject(d.setbacks);
  const setbackEntries   = setbacks
    ? Object.entries(setbacks).filter(([, v]) => hasDisplayValue(v))
    : [];
  const hasPlanning = !!(
    hasDisplayValue(d.zoning) || hasDisplayValue(d.lep_name) ||
    hasPositiveNumber(d.height_limit) || hasPositiveNumber(d.height_proposed) ||
    hasPositiveNumber(d.fsr_limit) || hasPositiveNumber(d.fsr_proposed) ||
    hasPositiveNumber(d.submissions_received) || hasPositiveNumber(d.objections_count) ||
    hasPositiveNumber(d.conditions_count) ||
    clauseVariations.length || referralAgencies.length || planningClauses.length || setbackEntries.length
  );

  // ── f) Site constraints ──────────────────────────────────────────────────────
  const constraints = [
    { key: 'flood_prone',            label: 'Flood Prone'         },
    { key: 'bushfire_prone',         label: 'Bushfire Prone'      },
    { key: 'heritage_listed',        label: 'Heritage'            },
    { key: 'coastal_zone',           label: 'Coastal Zone'        },
    { key: 'contaminated',           label: 'Contaminated'        },
    { key: 'biodiversity_triggered', label: 'Biodiversity'        },
    { key: 'acid_sulfate_soils',     label: 'Acid Sulfate'        },
    { key: 'aboriginal_heritage',    label: 'Aboriginal Heritage' },
  ].filter(c => d[c.key] === true);
  const hasConstraints = constraints.length > 0;

  // ── g) Determination ─────────────────────────────────────────────────────────
  const hasDetermination = !!(
    hasDisplayValue(d.assessment_officer) || hasDisplayValue(d.determination_type) ||
    hasDisplayValue(d.application_type)
  );

  // ── h) Tracking ──────────────────────────────────────────────────────────────
  const trackingMilestones = safeArray(d.tracking_milestones);
  const renderableTrackingMilestones = trackingMilestones.filter(m => (
    hasDisplayValue(m?.description || m?.event || m?.milestone || m?.name || (typeof m === 'string' ? m : null))
  ));
  const hasTracking = renderableTrackingMilestones.length > 0;

  // ── i) Documents ─────────────────────────────────────────────────────────────
  const documentInputs = [
    ...safeArray(d.document_list),
    ...safeArray(d.pdf_source_urls),
  ];
  const seenDocumentUrls = new Set();
  const renderableDocuments = documentInputs
    .map((doc, index) => normaliseDocumentItem(doc, index, sourceBaseUrl))
    .filter(doc => hasDisplayValue(doc.name) && hasDisplayValue(doc.url))
    .filter(doc => {
      const key = String(doc.url).replace(/#.*$/, '');
      if (seenDocumentUrls.has(key)) return false;
      seenDocumentUrls.add(key);
      return true;
    });
  const hasDocuments = DOCUMENT_VIEWING_ENABLED && renderableDocuments.length > 0;

  // ── j) DA History ────────────────────────────────────────────────────────────
  const prevDAs  = safeArray(d.previous_da_numbers);
  const relCerts = safeArray(d.related_certificates);
  const relApps  = safeArray(d.related_applications);
  const hasHistory = !!(prevDAs.length || relCerts.length || relApps.length);

  // ── k) Trades — Trade tier only ──────────────────────────────────────────────
  const isTradeTier = tier === 'trade';
  const trades = isTradeTier ? safeArray(d.trades_required).filter(hasDisplayValue) : [];

  // Nothing to show at all
  if (!displayTrustedParties.length && !hasSupplyChain && !hasSpecialists && !hasProject && !hasSustainability &&
      !hasPlanning && !hasConstraints && !hasDetermination && !hasReviewedPdfReport &&
      !hasTracking && !hasDocuments && !hasHistory && !trades.length) {
    if (showEmpty) {
      return (
        <div style={{ padding: '14px 0', color: theme?.textMuted || '#9ca3af', fontSize: '13px', lineHeight: 1.5, fontFamily: "'DM Sans',sans-serif" }}>
          Pro Intelligence data is not yet available for this DA.
        </div>
      );
    }
    return null;
  }

  const dividerStyle = { borderTop: '1px solid #1e2538', margin: '4px 0' };
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: '12px 16px',
    marginBottom: '4px',
  };
  const subheadStyle = {
    fontSize: "var(--text-xs)", color: theme.textMuted, letterSpacing: '0.8px',
    textTransform: 'uppercase', fontWeight: '600', marginBottom: '6px',
    fontFamily: "'DM Sans',sans-serif",
  };

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{ marginTop: '14px', paddingTop: '14px', borderTop: '2px solid #d4a84a' }}
    >

      <p style={{ color: theme.textMuted, fontSize: 12 }}>Document observations may be incomplete or conflicting. Check the official council documents before relying on technical details. In-app PDF viewing is paused.</p>
      {getProRecordConflicts(appRecord, d).map(message => (
        <p key={message} role="note" style={{ color: theme.text, borderLeft: '3px solid #d4a84a', padding: '8px 12px', fontSize: 13 }}>{message}</p>
      ))}
      {/* ── a) Supply Chain — SBD trusted parties (preferred) or flat PDF columns ── */}
      {displayTrustedParties.length > 0 ? (
        <CollapsibleSection title="Supply Chain" isOpen={open.supplyChain} onToggle={() => tog('supplyChain')}>
          {displayTrustedParties.map((party, i) => (
            <TrustedPartyCard
              key={i}
              party={party}
              theme={theme}
              contactSources={restrictedContactSources}
              restrictedContactAvailability={restrictedContactAvailability}
              sourceBaseUrl={sourceBaseUrl}
              sourceContext={sourceContext}
            />
          ))}
        </CollapsibleSection>
      ) : hasSupplyChain && (
        <CollapsibleSection title="Supply Chain" isOpen={open.supplyChain} onToggle={() => tog('supplyChain')}>
          <ContactRow role="Builder"   name={d.builder_name}   company={d.builder_company}   phone={d.builder_phone}   email={d.builder_email}   licence={d.builder_licence}   theme={theme} sourceBaseUrl={sourceBaseUrl} sourceContext={sourceContext} />
          <ContactRow role="Architect" name={d.architect_name} company={d.architect_company} phone={d.architect_phone} email={d.architect_email} registration={d.architect_registration} theme={theme} sourceBaseUrl={sourceBaseUrl} sourceContext={sourceContext} />
          <ContactRow role="Certifier" name={d.certifier_name} company={d.certifier_company} phone={d.certifier_phone} email={d.certifier_email} registration={d.certifier_registration} theme={theme} sourceBaseUrl={sourceBaseUrl} sourceContext={sourceContext} />
          <ContactRow role="Engineer"  name={d.engineer_name}  company={d.engineer_company}  type={d.engineer_type}   theme={theme} sourceBaseUrl={sourceBaseUrl} sourceContext={sourceContext} />
          <ContactRow role="Planner"   name={d.planner_name}   company={d.planner_company}   theme={theme} sourceBaseUrl={sourceBaseUrl} sourceContext={sourceContext} />
          <ContactRow role="Surveyor"  name={d.surveyor_name}  company={d.surveyor_company}  theme={theme} sourceBaseUrl={sourceBaseUrl} sourceContext={sourceContext} />
          <ContactRow
            role="Owner"
            name={d.owner_name}
            sourceLinks={contactSourcesForRole(restrictedContactSources, 'owner')}
            hiddenContactAvailability={{
              phone: (safeObject(restrictedContactAvailability.owner) || {}).phone === true || isValidPhone(d.owner_phone),
              email: (safeObject(restrictedContactAvailability.owner) || {}).email === true || isValidEmail(d.owner_email),
            }}
            theme={theme}
            sourceBaseUrl={sourceBaseUrl}
            sourceContext={sourceContext}
            brokeredContact={BROKERED_CONTACT_ENABLED && (isValidPhone(d.owner_phone) || isValidEmail(d.owner_email)) ? { daId: d.id, role: 'owner' } : null}
          />
          <ContactRow
            role="Applicant"
            name={d.applicant_name}
            company={d.applicant_company}
            phone={applicantIsBusiness ? d.applicant_phone : null}
            email={applicantIsBusiness ? d.applicant_email : null}
            sourceLinks={applicantIsBusiness ? null : contactSourcesForRole(restrictedContactSources, 'applicant')}
            hiddenContactAvailability={applicantIsBusiness ? null : {
              phone: (safeObject(restrictedContactAvailability.applicant) || {}).phone === true || isValidPhone(d.applicant_phone),
              email: (safeObject(restrictedContactAvailability.applicant) || {}).email === true || isValidEmail(d.applicant_email),
            }}
            theme={theme}
            sourceBaseUrl={sourceBaseUrl}
            sourceContext={sourceContext}
            brokeredContact={BROKERED_CONTACT_ENABLED && (isValidPhone(d.applicant_phone) || isValidEmail(d.applicant_email)) ? { daId: d.id, role: 'applicant' } : null}
          />
        </CollapsibleSection>
      )}

      {/* ── b) Specialists ──────────────────────────────────────────────────── */}
      {hasSpecialists && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Specialists" isOpen={open.specialists} onToggle={() => tog('specialists')}>
            {consultants.map((c, i) => (
              <ContactRow
                key={i}
                role={c.role || 'Consultant'}
                name={c.name || c.company || '—'}
                company={c.name && c.company ? c.company : null}
                phone={c.phone}
                email={c.email}
                theme={theme}
              />
            ))}
          </CollapsibleSection>
        </>
      )}

      {/* ── c) Project Details ──────────────────────────────────────────────── */}
      {hasProject && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Project" isOpen={open.project} onToggle={() => tog('project')}>
            <div style={gridStyle}>
              <GridItem label="Construction Type"       value={d.construction_type}        theme={theme} />
              <GridItem label="Building Classification" value={d.building_classification}  theme={theme} />
              <GridItem label="Materials"               value={d.construction_materials}   theme={theme} />
              <GridItem label="Gross Floor Area"
                value={hasPositiveNumber(d.gross_floor_area) ? `${Number(d.gross_floor_area).toLocaleString()} m²` : null}
                theme={theme}
              />
              <GridItem label="Site Area"
                value={hasPositiveNumber(d.site_area) ? `${Number(d.site_area).toLocaleString()} m²` : null}
                theme={theme}
              />
              <GridItem label="Car Spaces"      value={hasPositiveNumber(d.car_spaces) ? String(d.car_spaces) : null}             theme={theme} />
              <GridItem label="Accessible"      value={hasPositiveNumber(d.accessible_spaces) ? String(d.accessible_spaces) : null} theme={theme} />
              <GridItem label="Bicycle Spaces"  value={hasPositiveNumber(d.bicycle_spaces) ? String(d.bicycle_spaces) : null}     theme={theme} />
              <GridItem label="Existing Use"    value={d.existing_use}   theme={theme} />
            </div>
            {hasDisplayValue(d.conditions_summary) && (
              <div style={{ marginTop: '8px', fontSize:"var(--text-sm)", color: theme.textMuted, lineHeight: '1.6', fontFamily: "'DM Sans',sans-serif" }}>
                <span style={{ color: theme.textDim, textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: 'var(--text-xs)', fontWeight: 600 }}>
                  Key requirements
                </span>
                <br />
                {truncateText(d.conditions_summary, 260)}
              </div>
            )}
          </CollapsibleSection>
        </>
      )}

      {/* Independently reviewed, non-contact PDF facts */}
      {hasReviewedPdfReport && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Document Intelligence" isOpen={open.documentIntelligence} onToggle={() => tog('documentIntelligence')}>
            <PdfDocumentIntelligence
              report={d.pdf_intelligence_report}
              theme={theme}
              sourceBaseUrl={sourceBaseUrl}
              sourceContext={sourceContext}
            />
          </CollapsibleSection>
        </>
      )}

      {/* ── d) Sustainability ────────────────────────────────────────────────── */}
      {hasSustainability && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Sustainability" isOpen={open.sustainability} onToggle={() => tog('sustainability')}>
            <div style={gridStyle}>
              <GridItem label="BASIX Cert"     value={d.basix_certificate_number} theme={theme} />
              <GridItem label="BASIX Water"    value={hasPositiveNumber(d.basix_water_score)    ? String(d.basix_water_score)    : null} theme={theme} />
              <GridItem label="BASIX Energy"   value={hasPositiveNumber(d.basix_energy_score)   ? String(d.basix_energy_score)   : null} theme={theme} />
              <GridItem label="BASIX Thermal"  value={hasPositiveNumber(d.basix_thermal_score)  ? String(d.basix_thermal_score)  : null} theme={theme} />
              <GridItem label="NatHERS"
                value={hasPositiveNumber(d.nathers_rating) ? `${d.nathers_rating} stars` : null}
                amber theme={theme}
              />
              <GridItem label="NatHERS Assessor" value={d.nathers_assessor} theme={theme} />
              <GridItem label="Section J"      value={d.section_j_compliant === true ? 'Yes' : null} theme={theme} />
              <GridItem label="Solar Panels"
                value={d.solar_panels === true
                  ? (hasPositiveNumber(d.solar_capacity_kw) ? `${d.solar_capacity_kw} kW` : 'Yes')
                  : null}
                theme={theme}
              />
              <GridItem label="Rainwater Tank"
                value={d.rainwater_tank === true
                  ? (hasPositiveNumber(d.rainwater_capacity_litres) ? `${Number(d.rainwater_capacity_litres).toLocaleString()} L` : 'Yes')
                  : null}
                theme={theme}
              />
              <GridItem label="EV Charging"   value={d.ev_charging === true ? 'Yes' : null} theme={theme} />
              <GridItem label="Green Star"    value={d.green_star_rating} theme={theme} />
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* ── e) Planning Controls ────────────────────────────────────────────── */}
      {hasPlanning && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Planning" isOpen={open.planning} onToggle={() => tog('planning')}>
            <div style={{ ...gridStyle, marginBottom: (setbackEntries.length || clauseVariations.length || planningClauses.length || referralAgencies.length) ? '12px' : '4px' }}>
              <GridItem label="Zoning"  value={d.zoning}   theme={theme} />
              <GridItem label="Instrument" value={d.lep_name} theme={theme} />
              <GridItem label="Height"
                value={
                  hasPositiveNumber(d.height_proposed) && hasPositiveNumber(d.height_limit)
                    ? `${d.height_proposed}m proposed (${d.height_limit}m limit)`
                    : hasPositiveNumber(d.height_proposed) ? `${d.height_proposed}m proposed`
                    : hasPositiveNumber(d.height_limit)    ? `${d.height_limit}m limit`
                    : null
                }
                amber={!!(d.height_proposed && d.height_limit && Number(d.height_proposed) > Number(d.height_limit))}
                theme={theme}
              />
              <GridItem label="FSR"
                value={
                  hasPositiveNumber(d.fsr_proposed) && hasPositiveNumber(d.fsr_limit)
                    ? `${d.fsr_proposed} proposed (${d.fsr_limit} limit)`
                    : hasPositiveNumber(d.fsr_proposed) ? `${d.fsr_proposed} proposed`
                    : hasPositiveNumber(d.fsr_limit)    ? `${d.fsr_limit} limit`
                    : null
                }
                amber={!!(d.fsr_proposed && d.fsr_limit && Number(d.fsr_proposed) > Number(d.fsr_limit))}
                theme={theme}
              />
              <GridItem label="Lot/DP"
                value={formatLotPlan(d.lot_number, d.dp_number)}
                theme={theme}
              />
              <GridItem label="Submissions"
                value={
                  hasPositiveNumber(d.submissions_received)
                    ? `${d.submissions_received} received${hasPositiveNumber(d.objections_count) ? ` (${d.objections_count} objection${Number(d.objections_count) !== 1 ? 's' : ''})` : ''}`
                    : null
                }
                theme={theme}
              />
              <GridItem label="Conditions"  value={hasPositiveNumber(d.conditions_count) ? String(d.conditions_count) : null} theme={theme} />
              <GridItem label="Variations"  value={rawClauseVariations.length ? `${rawClauseVariations.length} identified` : null} theme={theme} />
              <GridItem label="Referrals"   value={rawReferralAgencies.length ? `${uniqueTextList(rawReferralAgencies).length} agencies` : null} theme={theme} />
            </div>

            {setbackEntries.length > 0 && (
              <div style={{ marginBottom: (clauseVariations.length || planningClauses.length || referralAgencies.length) ? '10px' : 0 }}>
                <div style={subheadStyle}>Setbacks</div>
                <div style={{ fontSize:"var(--text-sm)", color: '#e8e0d8', fontFamily: "'DM Sans',sans-serif", lineHeight: '1.6' }}>
                  {setbackEntries.map(([key, val]) =>
                    `${key.charAt(0).toUpperCase() + key.slice(1)}: ${hasPositiveNumber(val) ? `${val}m` : val}`
                  ).join(' · ')}
                </div>
              </div>
            )}

            {clauseVariations.length > 0 && (
              <div style={{ marginBottom: (planningClauses.length || referralAgencies.length) ? '10px' : 0 }}>
                <div style={subheadStyle}>Clause Variations</div>
                {clauseVariations.map((cv, i) => (
                  <div key={i} style={{ marginBottom: '8px', paddingLeft: '8px', borderLeft: '2px solid #d4a84a44' }}>
                    <div style={{ fontSize:"var(--text-sm)", color: '#e8e0d8', fontWeight: '600', fontFamily: "'DM Sans',sans-serif" }}>
                      {truncateText(cv.clause, 72)}{cv.description ? ` — ${truncateText(cv.description, 80)}` : ''}
                    </div>
                    {cv.variation_sought && (
                      <div style={{ fontSize:"var(--text-xs)", color: theme.textMuted, marginTop: '2px', fontFamily: "'DM Sans',sans-serif" }}>
                        Variation: {truncateText(cv.variation_sought, 150)}
                      </div>
                    )}
                  </div>
                ))}
                {rawClauseVariations.length > clauseVariations.length && (
                  <div style={{ fontSize: 'var(--text-xs)', color: theme.textDim, fontFamily: "'DM Sans',sans-serif" }}>
                    +{rawClauseVariations.length - clauseVariations.length} more variations
                  </div>
                )}
              </div>
            )}

            {planningClauses.length > 0 && (
              <div style={{ marginBottom: referralAgencies.length ? '10px' : 0 }}>
                <div style={subheadStyle}>Planning Clauses</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {planningClauses.map((clause, i) => (
                    <span key={i} title={clause} style={{
                      fontSize: 'var(--text-xs)', color: theme.textMuted, border: '1px solid #1e2538',
                      borderRadius: 6, padding: '4px 7px', fontFamily: "'DM Sans',sans-serif",
                    }}>
                      {truncateText(clause, 52)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {referralAgencies.length > 0 && (
              <div>
                <div style={subheadStyle}>Referrals</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {referralAgencies.map((agency, i) => (
                    <span key={i} title={agency} style={{
                      fontSize: 'var(--text-xs)', color: theme.textMuted, border: '1px solid #1e2538',
                      borderRadius: 6, padding: '4px 7px', fontFamily: "'DM Sans',sans-serif",
                    }}>
                      {truncateText(agency, 46)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CollapsibleSection>
        </>
      )}

      {/* ── f) Site Constraints ─────────────────────────────────────────────── */}
      {hasConstraints && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Site Constraints" isOpen={open.constraints} onToggle={() => tog('constraints')}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {constraints.map(c => (
                <span key={c.key} style={{
                  fontSize:"var(--text-xs)", padding: '4px 10px', borderRadius: '20px',
                  border: '1px solid #d4a84a66', color: '#d4a84a',
                  background: '#d4a84a10', fontFamily: "'DM Sans',sans-serif",
                  whiteSpace: 'nowrap',
                }}>
                  {c.label}
                </span>
              ))}
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* ── g) Determination ────────────────────────────────────────────────── */}
      {hasDetermination && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Determination" isOpen={open.determination} onToggle={() => tog('determination')}>
            <div style={gridStyle}>
              <GridItem label="Assessment Officer" value={d.assessment_officer} theme={theme} />
              <GridItem label="Determination Type" value={d.determination_type} theme={theme} />
              <GridItem label="Source application type"   value={d.application_type}   theme={theme} />
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* ── h) Tracking ─────────────────────────────────────────────────────── */}
      {hasTracking && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Tracking" isOpen={open.tracking} onToggle={() => tog('tracking')}>
            <div style={{ paddingLeft: '4px' }}>
              {renderableTrackingMilestones.map((m, i) => {
                const rawDate = m.completed || m.commenced || m.date || m.event_date || m.created_at || null;
                const label   = m.description || m.event || m.milestone || m.name || String(m);
                const dateStr = rawDate
                  ? new Date(rawDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                  : null;
                const isLast = i === renderableTrackingMilestones.length - 1;
                return (
                  <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: isLast ? 0 : '10px', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                      <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#d4a84a', marginTop: '5px' }} />
                      {!isLast && (
                        <div style={{ width: '1px', flex: 1, background: '#1e2538', marginTop: '4px', minHeight: '12px' }} />
                      )}
                    </div>
                    <div style={{ flex: 1, fontSize:"var(--text-sm)", color: '#e8e0d8', fontFamily: "'DM Sans',sans-serif", paddingTop: '2px' }}>
                      {dateStr && (
                        <span style={{ color: theme.textMuted }}>{dateStr}{' \u2014 '}</span>
                      )}
                      {label}
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* ── i) Documents ────────────────────────────────────────────────────── */}
      {hasDocuments && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Documents" isOpen={open.documents} onToggle={() => tog('documents')}>
            <div>
              {renderableDocuments.map((doc, i) => {
                const name = doc.name;
                const url = doc.url;
                const href = sourceHref(
                  { document_url: url, document_name: name },
                  sourceBaseUrl,
                  sourceContext
                );
                const isLast = i === renderableDocuments.length - 1;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: isLast ? 0 : '5px' }}>
                    <span style={{ color: theme.textMuted, fontSize:"var(--text-xs)", flexShrink: 0, marginTop: '3px' }}>▸</span>
                    {url ? (
                      <a href={href} onClick={e => e.stopPropagation()}
                        style={{ fontSize:"var(--text-sm)", color: '#d4a84a', fontFamily: "'DM Sans',sans-serif", textDecoration: 'none', lineHeight: '1.5' }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                      >
                        {name}
                      </a>
                    ) : (
                      <span style={{ fontSize:"var(--text-sm)", color: '#e8e0d8', fontFamily: "'DM Sans',sans-serif", lineHeight: '1.5' }}>{name}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* ── j) DA History ───────────────────────────────────────────────────── */}
      {hasHistory && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="History" isOpen={open.history} onToggle={() => tog('history')}>
            <div style={gridStyle}>
              {prevDAs.length > 0 && (
                <GridItem label="Related DAs"    value={prevDAs.join(', ')}   theme={theme} />
              )}
              {d.modification_number && (
                <GridItem label="Modification"   value={d.modification_number} theme={theme} />
              )}
              <RelatedRecordList label="Certificates" records={relCerts} theme={theme} />
              <RelatedRecordList label="Related Records" records={relApps} theme={theme} />
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* ── k) Trades Required — Trade tier only ────────────────────────────── */}
      {trades.length > 0 && (
        <>
          <div style={dividerStyle} />
          <CollapsibleSection title="Trades" isOpen={open.trades} onToggle={() => tog('trades')}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {trades.map((t, i) => (
                <span key={i} style={{
                  fontSize:"var(--text-xs)", padding: '3px 9px', borderRadius: '20px',
                  border: '1px solid #1e2538', color: theme.textMuted,
                  background: 'transparent', fontFamily: "'DM Sans',sans-serif",
                  textTransform: 'lowercase',
                }}>
                  {t}
                </span>
              ))}
            </div>
          </CollapsibleSection>
        </>
      )}


    </div>
  );
}
